/**
 * Run detail API module — typed fetch functions for the Run Detail view.
 */

import { apiFetch, apiFetchWithMeta, createEventSource } from './client.js'
import type {
  RunOverview,
  StageDetail,
  ParsedEvent,
  EventsQuery,
  RunConfig,
  RunState,
  RunSSEFrame,
} from '../types/api.js'

// ─── Coarse v2 endpoints ────────────────────────────────────────────────────

/** Fetch the full run overview in one call. */
export const fetchRunOverview = (runId: string): Promise<RunOverview> =>
  apiFetch<RunOverview>(`/api/v2/runs/${enc(runId)}/overview`)

/** Fetch stage detail on demand (when user clicks a graph node). */
export const fetchStageDetail = (runId: string, stageId: string): Promise<StageDetail> =>
  apiFetch<StageDetail>(`/api/v2/runs/${enc(runId)}/stages/${enc(stageId)}`)

/** Fetch paginated events for the Timeline tab. Returns data + pagination meta. */
export function fetchRunEvents(
  runId: string,
  query?: Partial<EventsQuery>,
): Promise<{
  data: ParsedEvent[]
  meta?: { page?: number; page_size?: number; total?: number; has_more?: boolean }
}> {
  const params: Record<string, string | number | boolean | undefined> = {}
  if (query) {
    for (const [k, v] of Object.entries(query)) {
      if (v !== undefined) {
        params[k] = v
      }
    }
  }
  return apiFetchWithMeta<ParsedEvent[]>(`/api/v2/runs/${enc(runId)}/events`, params)
}

/** Fetch run configuration. */
export const fetchRunConfig = (runId: string): Promise<RunConfig> =>
  apiFetch<RunConfig>(`/api/v2/runs/${enc(runId)}/config`)

/** Fetch aggregated sidebar state (called when state_version increments). */
export const fetchRunState = (runId: string): Promise<RunState> =>
  apiFetch<RunState>(`/api/v2/runs/${enc(runId)}/state`)

// ─── SSE Stream ─────────────────────────────────────────────────────────────

/**
 * Open the unified run stream. Single connection carries:
 * - new_event: tool calls, LLM responses (Viewing Deck)
 * - node_update: stage started/completed/failed (phase river)
 * - loop_completed: review loop finished (phase river)
 * - run_completed: run finished (phase river + status)
 *
 * All frame types carry state_version for sidebar refresh.
 */
export function openRunStream(
  runId: string,
  afterEventId: string | undefined,
  onFrame: (frame: RunSSEFrame) => void,
  onError?: (err: Event) => void,
): EventSource {
  const es = createEventSource(`/api/v2/runs/${enc(runId)}/stream`, {
    after: afterEventId,
  })

  const eventTypes = ['new_event', 'node_update', 'loop_completed', 'run_completed'] as const
  for (const type of eventTypes) {
    es.addEventListener(type, (e: MessageEvent) => {
      try {
        // SL-TS02 accepted: SSE data from our own backend
        const frame = JSON.parse(e.data) as RunSSEFrame
        onFrame(frame)
      } catch {
        // Malformed SSE frame — skip, do not close stream
      }
    })
  }

  if (onError) {
    es.addEventListener('error', onError)
  }

  return es
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function enc(s: string): string {
  return encodeURIComponent(s)
}
